import { createSlice } from "@reduxjs/toolkit"
import logger from "../Logger"
import { DARK_THEME, LIGHT_THEME } from "../themes/theme-mui"
import { TIMEZONE_LIST } from "../utilities/timeZone"

const pageLogger = logger.child({ store: "UI" })

// Theme preference: light is the default. A user's explicit toggle is persisted to
// localStorage via `setUserTheme`; transient screen-forced theming (`setTheme`, used by
// reports/dashboards) does NOT persist, so it never clobbers the saved preference.
const THEME_STORAGE_KEY = "ct-theme"
const getPersistedIsDark = () => {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(THEME_STORAGE_KEY) === "dark"
  } catch (e) {
    return false
  }
}
const initialIsDark = getPersistedIsDark() // default false → light

const UiSlice = createSlice({
  name: "UI",
  initialState: {
    currentTheme: initialIsDark ? DARK_THEME : LIGHT_THEME,
    isDarkMode: initialIsDark,
    brandColor: null, // org white-label accent; drives the top-level MUI primary palette
    showPrintable: false,
    showLoader: false,
    showModal: false,
    showDevTools: false,
    isLeftPanelVisible: false,
    isLeftPanelExpanded: true,
    // Hides the top Header + side rail + outer layout chrome so the active
    // route owns the whole viewport. Used by focus-mode pages like the
    // incident detail view. Default false → chrome shown.
    isChromeHidden: false,
    showFacilitatorPanel: false,
    showAdditionalInfoPanel: false,
    header: {
      showHeader: false,
      notification: {
        notificationList: [],
        count: 0,
        changed: false,
        showNotification: false,
      },
      gameView: {
        showGameView: false,
        isActive: false,
      },
    },
    timeZones: {
      zoneList: [],
      currentZone: "",
    },
  },
  reducers: {
    // Update Theme Mode
    setTheme: (state, action) => {
      document.querySelector("body").classList.remove("theme-dark", "theme-light")
      state.isDarkMode = action.payload === "light" ? false : true
      state.currentTheme = action.payload === "light" ? LIGHT_THEME : DARK_THEME
      document.querySelector("body").classList.add(action.payload === "light" ? "theme-light" : "theme-dark")
    },

    // Restore the user's preferred theme (persisted choice, defaulting to light). Used by
    // screens that transiently force a theme (reports/dashboards/print) when they exit —
    // previously they restored a hard-coded "dark" (the old default), which would now
    // wrongly flip light-preference users into dark.
    restoreUserTheme: (state) => {
      const isDark = getPersistedIsDark()
      document.querySelector("body").classList.remove("theme-dark", "theme-light")
      state.isDarkMode = isDark
      state.currentTheme = isDark ? DARK_THEME : LIGHT_THEME
      document.querySelector("body").classList.add(isDark ? "theme-dark" : "theme-light")
    },

    // User-initiated theme toggle: like setTheme but PERSISTS the choice so it survives
    // reloads. Use this from the header toggle; use setTheme for transient screen-forced
    // theming (reports/dashboards) that must not overwrite the user's preference.
    setUserTheme: (state, action) => {
      const mode = action.payload === "dark" ? "dark" : "light"
      document.querySelector("body").classList.remove("theme-dark", "theme-light")
      state.isDarkMode = mode === "dark"
      state.currentTheme = mode === "dark" ? DARK_THEME : LIGHT_THEME
      document.querySelector("body").classList.add(mode === "dark" ? "theme-dark" : "theme-light")
      try {
        localStorage.setItem(THEME_STORAGE_KEY, mode)
      } catch (e) {
        // localStorage unavailable (private mode) — preference just won't persist
      }
    },

    // White-label accent color (org brandColor). Set by AppLayout once org data loads so the
    // top-level ThemeProvider (CssBaseline, global Loader, all routes) shares the branded palette.
    setBrandColor: (state, action) => {
      state.brandColor = action.payload || null
    },

    // Preview Printable Version
    setShowPrintable: (state, action) => {
      state.showPrintable = action.payload ? true : false
    },
    // Loader Actions
    setShowLoader: (state, action) => {
      const loadingState = action.payload
      state.showLoader = loadingState
    },

    // Modal Actions
    setShowModal: (state, action) => {
      const showModalState = action.payload
      state.showModal = showModalState
    },

    // Dev Tools (e.g. React Query Tools)
    setShowDevTools: (state, action) => {
      const devToolsState = action.payload
      state.showDevTools = devToolsState
    },

    // Left Panel Visibility
    setIsLeftPanelVisible: (state, action) => {
      state.isLeftPanelVisible = action.payload ? true : false
    },

    // Left Panel Expansion
    setIsLeftPanelExpanded: (state, action) => {
      state.isLeftPanelExpanded = action.payload ? true : false
    },

    // Chrome (header + side rail) visibility for focus-mode pages.
    setIsChromeHidden: (state, action) => {
      state.isChromeHidden = !!action.payload
    },

    setShowFacilitatorPanel: (state, action) => {
      state.showFacilitatorPanel = !!action.payload
    },
    setShowAdditionalInfoPanel: (state, action) => {
      state.showAdditionalInfoPanel = !!action.payload
    },
    toggleFacilitatorPanel: (state) => {
      state.showFacilitatorPanel = !state.showFacilitatorPanel
      if (state.showFacilitatorPanel) state.showAdditionalInfoPanel = false
    },
    toggleAdditionalInfoPanel: (state) => {
      state.showAdditionalInfoPanel = !state.showAdditionalInfoPanel
      if (state.showAdditionalInfoPanel) state.showFacilitatorPanel = false
    },

    // Header Actions
    setShowHeader: (state) => {
      state.header.showHeader = !state.header.showHeader
    },

    // Notification Actions
    setShowNotification: (state) => {
      state.header.notification.showNotification = !state.header.notification.showNotification
    },

    addNotifications: (state, action) => {
      state.header.notification.changed = true
      const newNotification = action.payload
      state.header.notification.notificationList.push(newNotification)
      state.header.notification.count += 1
    },

    removeNotification: (state, action) => {
      state.header.notification.changed = true
      const id = action.payload
      // TODO: update condition find the relevant notifation to be removed
      const existingItem = state.header.notification.notificationList.find((item) => !id)
      if (existingItem) {
        state.header.notification.notificationList = state.header.notification.notificationList.filter((item) => item)
        state.header.notification.count -= 1
      } else {
        pageLogger.error(`Notification cannot be found to be removed!`)
      }
    },

    updateNotification: (state, action) => {},

    // GameView Actions
    setShowGameView: (state, action) => {
      state.header.gameView.showGameView = !!action.payload
    },

    setGameViewActive: (state, action) => {
      state.header.gameView.isActive = !!action.payload
    },

    // TimeZone Actions
    populateTimeZones: (state) => {
      const _timeZoneData = [...new Set(TIMEZONE_LIST.map((data) => data?.abbr))].sort().map((zone) => {
        return {
          display: zone,
          value: zone,
        }
      })
      const _currentZoneData = TIMEZONE_LIST.find((ele) => {
        const zone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone
        return ele?.utc?.includes(zone)
      })

      pageLogger.info({ timeZoneData: _timeZoneData }, `received timeZoneData`)
      pageLogger.info({ currentZone: _currentZoneData?.abbr }, `currentTimeZone`)
      state.timeZones.zoneList = _timeZoneData
      state.timeZones.currentZone = _currentZoneData?.abbr || "UTC"
    },
  },
})

export const UiActions = UiSlice.actions

export default UiSlice
